import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ALL_ROLES, ROLE_LABELS } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", name: "", organization: "", role: "community",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register(form);
      navigate("/");
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      <div className="w-full max-w-lg">
        <div className="overline mb-3">Provisioning</div>
        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">Create account</h2>
        <p className="mt-2 text-sm text-slate-500">Your account will be validated by an administrator.</p>

        <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="overline block mb-2">Full name</label>
            <input data-testid="reg-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full border border-slate-300 px-4 py-3 focus:border-[#0033A0] focus:outline-none" />
          </div>
          <div>
            <label className="overline block mb-2">Email</label>
            <input data-testid="reg-email" required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              className="w-full border border-slate-300 px-4 py-3 focus:border-[#0033A0] focus:outline-none font-mono text-sm" />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input data-testid="reg-password" required type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
              className="w-full border border-slate-300 px-4 py-3 focus:border-[#0033A0] focus:outline-none font-mono text-sm" />
          </div>
          <div>
            <label className="overline block mb-2">Organization</label>
            <input data-testid="reg-org" value={form.organization} onChange={e => setForm({...form, organization: e.target.value})}
              className="w-full border border-slate-300 px-4 py-3 focus:border-[#0033A0] focus:outline-none" />
          </div>
          <div>
            <label className="overline block mb-2">Role</label>
            <select data-testid="reg-role" value={form.role} onChange={e => setForm({...form, role: e.target.value})}
              className="w-full border border-slate-300 px-4 py-3 focus:border-[#0033A0] focus:outline-none">
              {ALL_ROLES.filter(r => r !== "admin").map(r => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
          {err && <div className="sm:col-span-2 border border-red-300 bg-red-50 text-red-700 text-sm px-4 py-3">{err}</div>}
          <div className="sm:col-span-2">
            <button data-testid="reg-submit" disabled={loading}
              className="w-full bg-[#0033A0] text-white py-3 flex items-center justify-center gap-2 hover:bg-slate-900 transition-colors disabled:opacity-60">
              {loading ? "Creating…" : "Create account"} <ArrowRight size={16} />
            </button>
          </div>
        </form>

        <div className="mt-6 text-xs text-slate-500">
          Already have access? <Link data-testid="to-login" to="/login" className="text-[#0033A0] font-semibold underline">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
